import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isOfflineGap } from '@/offline/availability';
import { savedWork } from '@/offline/engine';
import { localId, newOperationId, type NewOperation } from '@/offline/outbox';
import { useEffect, useRef, useState } from 'react';
import type { components } from '@/api/schema';
import { AppIcon, type IconKind } from '@/components/AppIcon';
import { StorePicker } from '@/components/StorePicker';
import { SearchSelect } from '@/components/SearchSelect';
import { BinSelect } from '@/components/BinSelect';
import {
  DialogSubject,
  ErrorNote,
  Field,
  FormActions,
  Modal,
  Select,
  errorMessage,
  rate,
  quantity,
  shortDate,
  todayIso,
} from '@/components/ui';
import { useBatches, useItemsById, useLastCosts, useStockInvalidation, useStores, useSuppliers } from '@/hooks/inventory';
import { PackedQtyInput } from '@/components/PackedQtyInput';
import { packedSuffix } from '@/packing';
import { KEPT_ON_THIS_PC, saveDocument } from '@/hooks/stockSend';
import { useAuth } from '@/stores/auth';
import { useShellNav } from '@/stores/shellNav';
import { VehicleField } from '@/components/VehicleField';

type StockRow = components['schemas']['StockBalanceItemDto'];

export type StockActionKind = 'receive' | 'issue' | 'transfer' | 'adjust';

const TITLES: Record<StockActionKind, string> = {
  receive: 'Receive (Stock In)',
  issue: 'Issue (Stock Out)',
  transfer: 'Transfer',
  adjust: 'Adjust',
};

const ICON: Record<StockActionKind, IconKind> = {
  receive: 'inbound',
  issue: 'outbound',
  transfer: 'transfer',
  adjust: 'adjust',
};

/** Sentinel for "register this supplier when the receipt is saved" — kept
 *  local rather than shared with Inward.tsx's own form, since the two forms
 *  never exchange this value directly. */
const NEW_SUPPLIER = '__new__';

/**
 * The quick action opened from a right-click on the Stock grid: receive,
 * issue, transfer or adjust one item without leaving the screen.
 *
 * Every field the row already answers is filled in and not asked again —
 * store, item, and the lot when one specific lot was selected. On success
 * the grid, tiles and navigator counts refresh through
 * `useStockInvalidation()`, and the caller keeps the row selected so the
 * detail pane shows the movement that was just made.
 */
export function StockActionDialog({
  action,
  row,
  onClose,
  onSaved,
}: {
  action: StockActionKind;
  row: StockRow;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  // Rows from the Stock grid's "By bin" view carry the bin keys, even when null.
  const binRow = row.binId !== undefined;
  return (
    <Modal title={TITLES[action]} onClose={onClose}>
      <h2 className="withicon">
        <AppIcon kind={ICON[action]} size={18} />
        {TITLES[action]}
      </h2>
      <DialogSubject
        icon="item"
        code={row.skuCode}
        name={row.skuName}
        nameUr={row.skuNameUr}
        tags={[
          { icon: 'store', label: `${row.storeCode} · ${row.store}` },
          row.batchNo
            ? {
                icon: 'lots',
                label: `Lot ${row.batchNo}${row.expiryDate ? ` · expires ${shortDate(row.expiryDate)}` : ''}`,
              }
            : null,
          binRow && { icon: 'bin', label: row.binCode ? `Bin ${row.binCode}` : 'Loose' },
        ]}
        figure={{
          label: binRow ? (row.binCode ? 'In this bin now' : 'Loose now') : 'On hand now',
          value: quantity(row.qty, row.uomPrecision),
          unit: row.uom,
          flag: row.belowThreshold && (
            <span className="pill pill--alert">
              <AppIcon kind="attention" size={12} />
              Low stock
            </span>
          ),
        }}
        // Only receiving picks a bin; everything else draws from the store's
        // bins oldest-filled first, so a bin row must not suggest otherwise.
        note={binRow && action !== 'receive' ? 'Taken from any bin in this store, oldest first.' : undefined}
      />

      {action === 'receive' && <ReceiveFields row={row} onClose={onClose} onSaved={onSaved} />}
      {action === 'issue' && <IssueFields row={row} onClose={onClose} onSaved={onSaved} />}
      {action === 'transfer' && <TransferFields row={row} onClose={onClose} onSaved={onSaved} />}
      {action === 'adjust' && <AdjustFields row={row} onClose={onClose} onSaved={onSaved} />}
    </Modal>
  );
}

/** A lot picker for a row that named no specific lot (the grouped "by item"
 *  view) — absent when the row already fixes one, or the item is not
 *  tracked by lot at all. */
function useLotChoice(row: StockRow) {
  const [batchId, setBatchId] = useState(row.batchId ?? '');
  const needsChoice = row.isBatchTracked && !row.batchId;
  const lotsQuery = useBatches(row.storeId, row.skuId, needsChoice);
  const { data: batches } = lotsQuery;

  const field = needsChoice ? (
    <Select
      label="Lot"
      required
      value={batchId}
      onChange={setBatchId}
      prompt={batches ? 'Choose a lot…' : isOfflineGap(lotsQuery) ? 'Lots not loaded on this PC' : 'Loading…'}
      options={(batches ?? []).map((b) => ({
        value: b.id,
        label: `${b.batchNo} — ${quantity(b.qty, row.uomPrecision)} on hand${
          b.expiryDate ? `, expires ${shortDate(b.expiryDate)}` : ''
        }`,
      }))}
      hint={batches && batches.length === 0 ? 'No lots of this item are held here' : undefined}
    />
  ) : null;

  return { batchId: row.isBatchTracked ? batchId : '', field, ready: !needsChoice || batchId !== '' };
}

// -------------------------------------------------------------- receive

function ReceiveFields({
  row,
  onClose,
  onSaved,
}: {
  row: StockRow;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  // The item's packages, so the quantity can be typed and read in them.
  const packed = useItemsById().get(row.skuId);
  const { can } = useAuth();
  const invalidate = useStockInvalidation();
  const queryClient = useQueryClient();
  const navigate = useShellNav((s) => s.navigate);
  const { data: suppliers } = useSuppliers();
  const lastCosts = useLastCosts([row.skuId]);
  const lastCost = lastCosts.data?.find((c) => c.skuId === row.skuId)?.lastCost;

  const [supplierId, setSupplierId] = useState('');
  const [newSupplierName, setNewSupplierName] = useState('');
  const [qty, setQty] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [batchNo, setBatchNo] = useState(row.batchNo ?? '');
  // Opened from a bin's row, the stock goes back into that bin by default.
  const [binId, setBinId] = useState(row.binId ?? '');
  const [invoiceRef, setInvoiceRef] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  // A new supplier typed here is added once, however often the dialog is saved.
  const madeSupplier = useRef<{ name: string; id: string } | null>(null);

  // Fills the cost box once the lookup resolves, same rule as the full
  // Receive stock form: only while it is still blank.
  useEffect(() => {
    if (lastCost && unitCost === '') setUnitCost(lastCost);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastCost]);

  const canPost = can('inv.inward.post');

  const save = useMutation({
    mutationFn: async (post: boolean) => {
      let resolvedSupplierId = supplierId;
      // A supplier typed here goes in the same batch as the receipt, ahead of
      // it (.docs/023): made at once with a line, kept on this PC without one.
      const before: NewOperation[] = [];
      if (resolvedSupplierId === NEW_SUPPLIER) {
        const name = newSupplierName.trim();
        if (madeSupplier.current?.name !== name) {
          const id = newOperationId();
          before.push({ id, kind: 'supplier.create', method: 'POST', path: '/api/v1/suppliers', body: { nameEn: name }, describes: `New supplier: ${name}`, screen: 'inv-reference' });
          madeSupplier.current = { name, id: localId(id) };
        }
        resolvedSupplierId = madeSupplier.current.id;
      }

      const body = {
        supplierId: resolvedSupplierId,
        storeId: row.storeId,
        ...(binId ? { binId } : {}),
        deliveryDate: todayIso(),
        invoiceRef: invoiceRef.trim() || undefined,
        lines: [
          {
            skuId: row.skuId,
            qty,
            unitCost: unitCost || '0',
            ...(batchNo.trim() ? { batchNo: batchNo.trim() } : {}),
          },
        ],
      };
      let doc;
      try {
        doc = await saveDocument({
          kind: 'inward',
          path: '/api/v1/inventory/inward',
          body,
          post,
          describes: `Stock IN ${todayIso()}: ${qty} of ${row.skuCode}`,
          screen: 'inv-inward',
          before,
        });
      } catch (e) {
        // Refused. A new supplier taken back with it is added again next time;
        // one the server already made is kept, and named by its id.
        const kept = madeSupplier.current && (await savedWork()).some((op) => localId(op.id) === madeSupplier.current!.id);
        if (!kept) madeSupplier.current = null;
        throw e;
      }
      if (before.length) void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      return { ...doc, posted: post };
    },
    onSuccess: (result) => {
      invalidate();
      onSaved(
        result.warning ?? (result.waiting ? KEPT_ON_THIS_PC : result.posted
          ? `${result.documentNo} posted. ${quantity(qty, row.uomPrecision)} ${row.uom} added to ${row.storeCode}.`
          : `${result.documentNo} saved as a draft. Someone with posting rights must post it before stock changes.`),
      );
    },
  });

  function submit(post: boolean) {
    if (!supplierId) return setProblem('Choose a supplier, or add a new one.');
    if (supplierId === NEW_SUPPLIER && !newSupplierName.trim()) return setProblem('Name the new supplier.');
    if (!(Number(qty) > 0)) return setProblem('Enter a quantity above zero.');
    setProblem(null);
    save.mutate(post);
  }

  const supplierOptions = [
    ...(suppliers ?? []).map((s) => ({ value: s.id, label: s.nameEn, meta: s.code })),
  ];

  return (
    <div className="stack">
      <SearchSelect
        label="Supplier"
        required
        value={supplierId}
        selectedLabel={supplierId === NEW_SUPPLIER ? `New supplier: ${newSupplierName}` : undefined}
        options={supplierOptions}
        placeholder="Search suppliers…"
        onChange={(option) => setSupplierId(option?.value ?? '')}
        onCreate={(name) => {
          setSupplierId(NEW_SUPPLIER);
          setNewSupplierName(name);
        }}
        createLabel={(name) => `+ Register "${name}" as a one-off supplier`}
      />
      {supplierId === NEW_SUPPLIER && (
        <Field label="New supplier name" required value={newSupplierName} onChange={setNewSupplierName} />
      )}

      <div className="formgrid formgrid--2">
        <PackedQtyInput
          label={`Quantity (${row.uom})`}
          item={packed}
          itemKey={row.skuId}
          startWith="package"
          required
          value={qty}
          onChange={setQty}
        />
        <Field
          label="Cost per unit"
          type="number"
          min={0}
          value={unitCost}
          onChange={setUnitCost}
          hint={
            lastCost
              ? `Last: ${rate(lastCost)}`
              : lastCosts.data && lastCosts.data[0]?.lastCost === null
                ? 'New item — no previous cost'
                : undefined
          }
        />
      </div>

      {row.isBatchTracked && (
        <Field
          label="Lot number"
          mono
          value={batchNo}
          onChange={setBatchNo}
          hint="Optional. An existing lot number adds to that lot, a new one starts a fresh lot, and a blank one uses the receipt number"
        />
      )}

      <BinSelect storeId={row.storeId} value={binId} onChange={setBinId} />

      <Field label="Invoice reference" value={invoiceRef} onChange={setInvoiceRef} />

      {problem && <ErrorNote message={problem} />}
      {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}

      <FormActions onCancel={onClose}>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => {
            onClose();
            navigate('inv-inward', { action: 'create', storeId: row.storeId, skuId: row.skuId });
          }}
        >
          Receiving many items?
        </button>
        {canPost ? (
          <button type="button" className="btn btn--primary" disabled={save.isPending} onClick={() => submit(true)}>
            {save.isPending ? 'Saving…' : 'Save and post'}
          </button>
        ) : (
          <button type="button" className="btn btn--primary" disabled={save.isPending} onClick={() => submit(false)}>
            {save.isPending ? 'Saving…' : 'Save draft'}
          </button>
        )}
      </FormActions>
    </div>
  );
}

// ---------------------------------------------------------------- issue

function IssueFields({
  row,
  onClose,
  onSaved,
}: {
  row: StockRow;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  // The item's packages, so the quantity can be typed and read in them.
  const packed = useItemsById().get(row.skuId);
  const { can } = useAuth();
  const invalidate = useStockInvalidation();
  const lot = useLotChoice(row);

  const [qty, setQty] = useState('');
  const [issuedTo, setIssuedTo] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const canPost = can('inv.consumption.post');
  const over = qty !== '' && Number(qty) > Number(row.qty);

  const save = useMutation({
    mutationFn: async (post: boolean) => {
      const body = {
        storeId: row.storeId,
        skuId: row.skuId,
        qty,
        consumedOn: todayIso(),
        ...(lot.batchId ? { batchId: lot.batchId } : {}),
        issuedTo: issuedTo.trim() || undefined,
      };
      const doc = await saveDocument({
        kind: 'consumption',
        path: '/api/v1/inventory/consumption',
        body,
        post,
        describes: `Stock OUT ${todayIso()}: ${qty} of ${row.skuCode}`,
        screen: 'inv-consume',
      });
      return { ...doc, posted: post };
    },
    onSuccess: (result) => {
      invalidate();
      onSaved(
        result.warning ?? (result.waiting ? KEPT_ON_THIS_PC : result.posted
          ? `${result.documentNo} posted. ${quantity(qty, row.uomPrecision)} ${row.uom} issued from ${row.storeCode}.`
          : `${result.documentNo} saved as a draft. Someone with posting rights must post it before stock changes.`),
      );
    },
  });

  function submit(post: boolean) {
    if (!(Number(qty) > 0)) return setProblem('Enter a quantity above zero.');
    if (over) return setProblem(`Only ${quantity(row.qty, row.uomPrecision)} ${row.uom} is held here.`);
    if (row.isBatchTracked && !lot.batchId) return setProblem('Choose which lot this is issued from.');
    setProblem(null);
    save.mutate(post);
  }

  return (
    <div className="stack">
      {lot.field}
      <PackedQtyInput
        label={`Quantity (${row.uom})`}
        item={packed}
        itemKey={row.skuId}
        remember="draw"
        required
        value={qty}
        onChange={setQty}
        hint={
          over ? (
            <span className="errornote">
              ⚠ More than the {quantity(row.qty, row.uomPrecision)} {row.uom}{packedSuffix(row.qty, packed)} held here.
            </span>
          ) : undefined
        }
      />
      <Field label="Issued to" value={issuedTo} onChange={setIssuedTo} placeholder="Shed 3, or a person's name" />

      {problem && <ErrorNote message={problem} />}
      {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}

      <FormActions onCancel={onClose}>
        {canPost ? (
          <button type="button" className="btn btn--primary" disabled={save.isPending} onClick={() => submit(true)}>
            {save.isPending ? 'Saving…' : 'Save and post'}
          </button>
        ) : (
          <button type="button" className="btn btn--primary" disabled={save.isPending} onClick={() => submit(false)}>
            {save.isPending ? 'Saving…' : 'Save draft'}
          </button>
        )}
      </FormActions>
    </div>
  );
}

// ------------------------------------------------------------- transfer

function TransferFields({
  row,
  onClose,
  onSaved,
}: {
  row: StockRow;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  // The item's packages, so the quantity can be typed and read in them.
  const packed = useItemsById().get(row.skuId);
  const { can } = useAuth();
  const invalidate = useStockInvalidation();
  const { data: stores } = useStores();
  const lot = useLotChoice(row);

  const [toStoreId, setToStoreId] = useState('');
  const [qty, setQty] = useState('');
  const [isReturn, setIsReturn] = useState(false);
  const [vehicleNo, setVehicleNo] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const canPost = can('inv.transfer.post');
  const over = qty !== '' && Number(qty) > Number(row.qty);

  const save = useMutation({
    mutationFn: async (post: boolean) => {
      const body = {
        fromStoreId: row.storeId,
        toStoreId,
        transferDate: todayIso(),
        isReturn,
        vehicleNo: vehicleNo.trim() || undefined,
        lines: [{ skuId: row.skuId, qty, ...(lot.batchId ? { batchId: lot.batchId } : {}) }],
      };
      const doc = await saveDocument({
        kind: 'transfer',
        path: '/api/v1/inventory/transfers',
        body,
        post,
        describes: `Transfer ${todayIso()}: ${qty} of ${row.skuCode}`,
        screen: 'inv-xfer',
      });
      return { ...doc, posted: post };
    },
    onSuccess: (result) => {
      invalidate();
      onSaved(
        result.warning ?? (result.waiting ? KEPT_ON_THIS_PC : result.posted
          ? `${result.documentNo} posted. ${quantity(qty, row.uomPrecision)} ${row.uom} moved from ${row.storeCode}.`
          : `${result.documentNo} saved as a draft. Someone with posting rights must post it before stock changes.`),
      );
    },
  });

  function submit(post: boolean) {
    if (!toStoreId) return setProblem('Choose the destination store.');
    if (toStoreId === row.storeId) return setProblem('A store cannot send stock to itself.');
    if (!(Number(qty) > 0)) return setProblem('Enter a quantity above zero.');
    if (over) return setProblem(`Only ${quantity(row.qty, row.uomPrecision)} ${row.uom} is held here.`);
    if (row.isBatchTracked && !lot.batchId) return setProblem('Choose which lot this is moved from.');
    setProblem(null);
    save.mutate(post);
  }

  const storeOptions = stores?.filter((s) => s.id !== row.storeId && s.isActive);

  return (
    <div className="stack">
      <StorePicker label="To store" required value={toStoreId} onChange={setToStoreId} stores={storeOptions} />
      {lot.field}
      <PackedQtyInput
        label={`Quantity (${row.uom})`}
        item={packed}
        itemKey={row.skuId}
        remember="draw"
        required
        value={qty}
        onChange={setQty}
        hint={
          over ? (
            <span className="errornote">
              ⚠ More than the {quantity(row.qty, row.uomPrecision)} {row.uom}{packedSuffix(row.qty, packed)} held here.
            </span>
          ) : undefined
        }
      />
      <VehicleField label="Vehicle" value={vehicleNo} onChange={setVehicleNo} placeholder="Registration number" />
      <label className="settings__toggle">
        <input type="checkbox" checked={isReturn} onChange={(e) => setIsReturn(e.target.checked)} />
        <span>This is a return</span>
      </label>

      {problem && <ErrorNote message={problem} />}
      {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}

      <FormActions onCancel={onClose}>
        {canPost ? (
          <button type="button" className="btn btn--primary" disabled={save.isPending} onClick={() => submit(true)}>
            {save.isPending ? 'Saving…' : 'Save and post'}
          </button>
        ) : (
          <button type="button" className="btn btn--primary" disabled={save.isPending} onClick={() => submit(false)}>
            {save.isPending ? 'Saving…' : 'Save draft'}
          </button>
        )}
      </FormActions>
    </div>
  );
}

// --------------------------------------------------------------- adjust

function AdjustFields({
  row,
  onClose,
  onSaved,
}: {
  row: StockRow;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  // The item's packages, so the quantity can be typed and read in them.
  const packed = useItemsById().get(row.skuId);
  const invalidate = useStockInvalidation();
  const lot = useLotChoice(row);

  const [direction, setDirection] = useState<'1' | '-1'>('1');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const over = direction === '-1' && qty !== '' && Number(qty) > Number(row.qty);

  const save = useMutation({
    mutationFn: () =>
      saveDocument({
        kind: 'adjustment',
        path: '/api/v1/inventory/adjustments',
        body: {
          storeId: row.storeId,
          adjustmentDate: todayIso(),
          reason: reason.trim(),
          lines: [
            {
              skuId: row.skuId,
              qty: `${direction === '-1' ? '-' : ''}${qty}`,
              ...(lot.batchId ? { batchId: lot.batchId } : {}),
            },
          ],
        },
        post: false,
        describes: `Adjustment ${todayIso()}: ${qty} of ${row.skuCode}, ${reason.trim()}`,
        screen: 'inv-adjust',
      }),
    onSuccess: (result) => {
      invalidate();
      // Whoever raises an adjustment can never approve it themselves — the
      // server refuses that outright — so this never offers "save and post".
      onSaved(
        result.waiting
          ? KEPT_ON_THIS_PC
          : `${result.documentNo} raised. Someone else must approve it before it can post.`,
      );
    },
  });

  function submit() {
    if (!(Number(qty) > 0)) return setProblem('Enter a quantity above zero.');
    if (over) return setProblem(`Only ${quantity(row.qty, row.uomPrecision)} ${row.uom} is held here to write off.`);
    if (row.isBatchTracked && !lot.batchId) return setProblem('Choose which lot this adjustment is for.');
    if (!reason.trim()) return setProblem('Say why this adjustment is needed — it is the whole audit trail.');
    setProblem(null);
    save.mutate();
  }

  return (
    <div className="stack">
      <div className="formgrid formgrid--2">
        <Select
          label="Direction"
          required
          value={direction}
          onChange={(v) => setDirection(v as '1' | '-1')}
          options={[
            { value: '1', label: 'Increase (found more than recorded)' },
            { value: '-1', label: 'Decrease (write off — damage, spillage, miscount)' },
          ]}
          placeholder=""
        />
        <PackedQtyInput
          label={`Quantity (${row.uom})`}
          item={packed}
          itemKey={row.skuId}
          remember="draw"
          required
          value={qty}
          onChange={setQty}
          hint={
            over ? (
              <span className="errornote">
                ⚠ More than the {quantity(row.qty, row.uomPrecision)} {row.uom}{packedSuffix(row.qty, packed)} held here.
              </span>
            ) : undefined
          }
        />
      </div>
      {lot.field}
      <Field
        label="Reason"
        required
        value={reason}
        onChange={setReason}
        placeholder="Damaged in the store, spillage during handling…"
      />

      {problem && <ErrorNote message={problem} />}
      {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}

      <FormActions onCancel={onClose}>
        <button type="button" className="btn btn--primary" disabled={save.isPending} onClick={submit}>
          {save.isPending ? 'Saving…' : 'Raise adjustment'}
        </button>
      </FormActions>
    </div>
  );
}
